import * as React from 'react';

import BhanzuBuddyIcon from '@/assets/images/bhanzuBuddy.svg';
import { cn } from '@/lib/utils';

import type { BhanzuBuddyChatMessage } from './bhanzu-buddy-chat-types';
import { LaTeXRenderer } from './latex-renderer';

/**
 * Individual Chat Message Component
 */
export const BhanzuBuddyChatMessageItem: React.FC<{
  message: BhanzuBuddyChatMessage;
  onFeedback?: (messageId: string, type: 'positive' | 'negative') => void;
  onGuideMe?: () => void;
}> = ({ message, onFeedback, onGuideMe }) => {
  // Use feedback state from message data (from API) or local state for new messages
  const [localFeedback, setLocalFeedback] = React.useState<
    'positive' | 'negative' | null
  >(null);

  // Get feedback state: prioritize message.feedbackGiven from API, fall back to local state
  const feedbackGiven = message.feedbackGiven ?? localFeedback;

  const handleFeedback = (type: 'positive' | 'negative') => {
    // Only update local state if message doesn't have API feedback state
    if (message.feedbackGiven === undefined) {
      setLocalFeedback(type);
    }
    onFeedback?.(message.id, type);
  };

  // User message - right aligned
  if (message.type === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-magical-moonlight-d2 text-white rounded-2xl rounded-br-md px-3 sm:px-4 py-2 sm:py-3 max-w-[85%] sm:max-w-[80%]">
          <LaTeXRenderer
            content={message.content}
            className="text-xs sm:text-sm leading-relaxed"
          />
        </div>
      </div>
    );
  }

  // BhanzuBuddy message - left aligned with avatar and exact Figma positioning
  return (
    <div className="flex items-start gap-2 sm:gap-3">
      {/* BhanzuBuddy Avatar */}
      <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-magical-moonlight-d3 border border-magical-moonlight-d2 flex items-center justify-center flex-shrink-0">
        <img
          src={BhanzuBuddyIcon}
          alt="Bhanzu Buddy"
          className="w-4 h-4 sm:w-6 sm:h-6"
        />
      </div>

      {/* Message Content */}
      <div className="flex-1 space-y-3">
        {message.isLoading ? (
          /* Loading Animation - 3 dots */
          <div className="flex items-center gap-1 py-2">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            <div
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '0.1s' }}
            ></div>
            <div
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '0.2s' }}
            ></div>
          </div>
        ) : (
          <>
            {/* Message Text */}
            <div className="text-[16px] leading-[24px] font-medium text-magical-moonlight-white">
              <LaTeXRenderer content={message.content} />
            </div>

            {/* Divider */}
            <div className="w-full h-px">
              <svg
                className="w-full h-full"
                viewBox="0 0 512 2"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M0 1H512"
                  stroke="currentColor"
                  strokeDasharray="2 2"
                  className="text-magical-moonlight-d2"
                />
              </svg>
            </div>

            {/* Feedback Section */}
            <div className="flex items-center gap-4">
              <span className="text-[16px] leading-[24px] font-medium text-magical-moonlight-d1">
                Did we answer your question?
              </span>
              <div className="flex items-center gap-4">
                {/* Thumbs Up */}
                <button
                  type="button"
                  onClick={() => handleFeedback('positive')}
                  className={cn(
                    'w-6 h-6 transition-colors',
                    feedbackGiven === 'positive'
                      ? 'text-blue-500'
                      : 'text-magical-moonlight-d1 hover:text-white'
                  )}
                  aria-label="Positive feedback"
                >
                  <svg
                    className="block w-full h-full"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M7 11V19C7 19.2652 6.89464 19.5196 6.70711 19.7071C6.51957 19.8946 6.26522 20 6 20H4C3.73478 20 3.48043 19.8946 3.29289 19.7071C3.10536 19.5196 3 19.2652 3 19V12C3 11.7348 3.10536 11.4804 3.29289 11.2929C3.48043 11.1054 3.73478 11 4 11H7ZM7 11C8.06087 11 9.07828 10.5786 9.82843 9.82843C10.5786 9.07828 11 8.06087 11 7V6C11 5.46957 11.2107 4.96086 11.5858 4.58579C11.9609 4.21071 12.4696 4 13 4C13.5304 4 14.0391 4.21071 14.4142 4.58579C14.7893 4.96086 15 5.46957 15 6V11H18C18.5304 11 19.0391 11.2107 19.4142 11.5858C19.7893 11.9609 20 12.4696 20 13L19 18C18.8562 18.6135 18.5834 19.1402 18.2227 19.501C17.8619 19.8617 17.4328 20.0368 17 20H10C9.20435 20 8.44129 19.6839 7.87868 19.1213C7.31607 18.5587 7 17.7956 7 17"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.25"
                    />
                  </svg>
                </button>
                {/* Thumbs Down */}
                <button
                  type="button"
                  onClick={() => handleFeedback('negative')}
                  className={cn(
                    'w-6 h-6 transition-colors rotate-180',
                    feedbackGiven === 'negative'
                      ? 'text-blue-500'
                      : 'text-magical-moonlight-d1 hover:text-white'
                  )}
                  aria-label="Negative feedback"
                >
                  <svg
                    className="block w-full h-full"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M7 11V19C7 19.2652 6.89464 19.5196 6.70711 19.7071C6.51957 19.8946 6.26522 20 6 20H4C3.73478 20 3.48043 19.8946 3.29289 19.7071C3.10536 19.5196 3 19.2652 3 19V12C3 11.7348 3.10536 11.4804 3.29289 11.2929C3.48043 11.1054 3.73478 11 4 11H7ZM7 11C8.06087 11 9.07828 10.5786 9.82843 9.82843C10.5786 9.07828 11 8.06087 11 7V6C11 5.46957 11.2107 4.96086 11.5858 4.58579C11.9609 4.21071 12.4696 4 13 4C13.5304 4 14.0391 4.21071 14.4142 4.58579C14.7893 4.96086 15 5.46957 15 6V11H18C18.5304 11 19.0391 11.2107 19.4142 11.5858C19.7893 11.9609 20 12.4696 20 13L19 18C18.8562 18.6135 18.5834 19.1402 18.2227 19.501C17.8619 19.8617 17.4328 20.0368 17 20H10C9.20435 20 8.44129 19.6839 7.87868 19.1213C7.31607 18.5587 7 17.7956 7 17"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.25"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Guide Me Link */}
            {Boolean(onGuideMe) && (
              <div className="text-[16px] leading-[24px] font-medium text-magical-moonlight-d1">
                <span>Do you need more help? Try </span>
                <button
                  type="button"
                  onClick={onGuideMe}
                  className="text-blue-chaos underline font-bold hover:text-blue-300 transition-colors"
                >
                  Guide me
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
